import { createRef } from 'react';
import {IContentBlock, IDesignBlock} from '../../types/iblock';
import { IHtmlBlockData } from '../../types/content-block-data';
import pageBuilderStore from '../../stores/pageBuilderStore';
import blocksManager from '../../stores/blocks-manager';
import contentManager from '../../stores/content-manager';

/**
 * Converts an HtmlBlock (IContentBlock) to an HTML string.
 * Renders the raw HTML, assuming it is trusted/sanitized upstream.
 *
 * @param block - The IContentBlock (should be blockType "html")
 * @returns The raw HTML string as provided by the user.
 * 
 * @remarks
 * - Added a new local variable `isEmail` for toggling serialization markup.
 */
export function fromHtmlBlockToHTML(block: IContentBlock): string {
  if (block.blockType !== "html") return "";

  const isEmail = pageBuilderStore.getIsEmail();
  const data = block.blockData as IHtmlBlockData;
  const paddingCss = data.padding?.value
    ? `padding: ${data.padding.value}${data.padding.unit || "px"};`
    : "";
  // Assumes data.html is already sanitized.
  return isEmail 
    ? `<tr><td class="${block.selector}" style="${paddingCss}">${data.html ?? ""}</td></tr>`
    : `<div class="${block.selector}" style="${paddingCss}">${data.html ?? ""}</div>`;
}

/**
 * Serializes an HtmlBlock's data for storage or export.
 * Omits UI-only fields.
 *
 * @param block - The IContentBlock (should be blockType "html")
 * @returns A JSON-serializable object
 */
export function fromHtmlBlockToJSON(block: IContentBlock): object {
  if (block.blockType !== "html") return {};
  const data = block.blockData as IHtmlBlockData;
  return {
    contentBlockId: block.contentBlockId,
    blockType: block.blockType,
    selector: block.selector,
    blockData: {
      html: data.html ?? "",
    },
  };
}

/**
 * Reconstructs an HtmlBlock from JSON data.
 *
 * @param json - The JSON object to parse
 * @returns An IContentBlock instance (for use in your store/UI)
 */
export function fromJSONToHtmlBlock(json: any, context: "canvas" | "columns" = "canvas"): IDesignBlock {
  const htmlStyles = contentManager.getStylesForSelector(json.selector);
    
  const newDesignBlockRef = createRef<HTMLDivElement>();
  const newContentBlockRef = createRef<HTMLElement>(); // generic for now

  const block: IDesignBlock = {
    designBlockId: crypto.randomUUID(), // always generate fresh
    designBlockRef: newDesignBlockRef,
    contentBlock: {
      contentBlockId: json.contentBlockId,
      blockType: json.blockType,
      contentBlockRef: newContentBlockRef,
      selector: json.selector,
      blockData: {
        html: json.blockData?.html ?? "",
        padding: blocksManager.parseDimensions(htmlStyles?.["padding"], "single"),
      } as IHtmlBlockData,
    },
  };

  blocksManager.addDesignTreeEntry({
		contentBlockId: block.contentBlock.contentBlockId,
		designBlockId: block.designBlockId,
		designBlockRef: newDesignBlockRef,
		contentBlockRef: newContentBlockRef,
		contentBlockType: block.contentBlock.blockType.toUpperCase(),
		location: context,
	});

  return block;
}
